import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Search, User, Copy } from 'lucide-react';
import AddressFinder from '../../AddressFinder';
import JobCategoryPicker from '../../JobCategoryPicker';
import ProductServicePicker from '../../ProductServicePicker';
import DateTimePicker from '../../DateTimePicker';
import TextAreaPopup from '../../TextAreaPopup';
import authService from '../../../services/authService';
import customerService from '../../../services/customerService';

const InvoiceForm = ({ onClose, onSubmit, invoice, isEditing = false, preselectedCustomer, preselectedJob, preselectedAppointment, showCustomerInTitle = false, user, mode = 'invoice' }) => {
  const isEstimate = mode === 'estimate';

  const [formData, setFormData] = useState({
    // Number & dates — map estimate fields to internal names
    doc_number: invoice?.invoice_number || invoice?.estimate_number || '',
    doc_date: (invoice?.invoice_date || invoice?.estimate_date)
      ? new Date(invoice?.invoice_date || invoice?.estimate_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    due_date: invoice?.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    expiry_date: invoice?.expiry_date ? new Date(invoice.expiry_date).toISOString().split('T')[0] : '',
    location: invoice?.location || '',
    class: invoice?.class || '',
    status: invoice?.status || (isEstimate ? 'active' : 'outstanding'),

    // Customer Information
    customer_id: invoice?.customer_id || '',
    customer_name: invoice?.customer_name || '',
    customer_email: invoice?.customer_email || '',

    // Billing Address
    bill_addr_line1: invoice?.bill_addr_line1 || '',
    bill_addr_line2: invoice?.bill_addr_line2 || '',
    bill_addr_city: invoice?.bill_addr_city || '',
    bill_addr_state: invoice?.bill_addr_state || '',
    bill_addr_postal_code: invoice?.bill_addr_postal_code || '',
    bill_addr_country: invoice?.bill_addr_country || 'United Kingdom',
    bill_company_name: invoice?.bill_company_name || '',

    // Treatment Address
    ship_addr_line1: invoice?.ship_addr_line1 || '',
    ship_addr_line2: invoice?.ship_addr_line2 || '',
    ship_addr_city: invoice?.ship_addr_city || '',
    ship_addr_state: invoice?.ship_addr_state || '',
    ship_addr_postal_code: invoice?.ship_addr_postal_code || '',
    ship_addr_country: invoice?.ship_addr_country || 'United Kingdom',
    ship_customer_name: invoice?.ship_customer_name || '',
    ship_company_name: invoice?.ship_company_name || '',

    // Financial
    terms: invoice?.terms || (isEditing && invoice?.due_date ? '' : 'Due on receipt'),
    memo: invoice?.memo || ''
  });

  const [lineItems, setLineItems] = useState(() => {
    if (invoice?.line_items?.length > 0) {
      return invoice.line_items.map((item, i) => ({
        ...item,
        id: item.id || i + 1,
        service_date: item.service_date || '',
        quantity: parseFloat(item.quantity) || 1,
        rate: parseFloat(item.rate) || 0,
        amount: parseFloat(item.amount) || 0,
        _useCustomQty: false
      }));
    }
    return [{
      id: 1,
      product_service: '',
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      _useCustomQty: false
    }];
  });

  const [errors, setErrors] = useState({});
  const [addressFinderStates, setAddressFinderStates] = useState({
    billing: {},
    treatment: {}
  });
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [editingDescriptionIndex, setEditingDescriptionIndex] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);

  // Job category picker state
  const [selectedJobCategoryId, setSelectedJobCategoryId] = useState(
    preselectedJob?.job_category_id || null
  );

  // Track if due_date was manually overridden
  const [dueDateManual, setDueDateManual] = useState(false);

  const statusOptions = isEstimate
    ? [
        { value: 'active', label: 'Active' },
        { value: 'declined', label: 'Declined' },
        { value: 'expired', label: 'Expired' },
        { value: 'converted', label: 'Converted to Invoice' }
      ]
    : [
        { value: 'draft', label: 'Draft' },
        { value: 'outstanding', label: 'Outstanding' },
        { value: 'sent', label: 'Sent' },
        { value: 'viewed', label: 'Viewed' },
        { value: 'partial', label: 'Partial' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'cancelled', label: 'Cancelled' }
      ];


  // Number is auto-generated by the backend on save — no pre-fetch needed

  // --- Auto-calculate due date based on terms (invoices only) ---
  useEffect(() => {
    if (isEstimate || dueDateManual || isEditing) return;
    if (!formData.doc_date) return;

    const invoiceDate = new Date(formData.doc_date);
    let dueDate;

    switch (formData.terms) {
      case 'Due on receipt':
        dueDate = new Date(invoiceDate);
        break;
      case 'Net 7':
        dueDate = new Date(invoiceDate);
        dueDate.setDate(dueDate.getDate() + 7);
        break;
      case 'Net 15':
        dueDate = new Date(invoiceDate);
        dueDate.setDate(dueDate.getDate() + 15);
        break;
      case 'Net 30':
        dueDate = new Date(invoiceDate);
        dueDate.setDate(dueDate.getDate() + 30);
        break;
      default:
        return;
    }

    setFormData(prev => ({ ...prev, due_date: dueDate.toISOString().split('T')[0] }));
  }, [formData.terms, formData.doc_date, dueDateManual, isEditing, isEstimate]);


  // Calculate totals
  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxRate = 0.20; // 20% VAT
    const taxAmount = subtotal * taxRate;
    const total = subtotal + taxAmount;

    return {
      subtotal: subtotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If user manually changes due_date, mark as manual
    if (name === 'due_date') {
      setDueDateManual(true);
    }
    // If user changes terms or doc_date, reset manual override so auto-calc kicks in
    if (name === 'terms' || name === 'doc_date') {
      setDueDateManual(false);
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Recalculate amount when quantity or rate changes
    if (field === 'quantity' || field === 'rate') {
      const quantity = parseFloat(updatedItems[index].quantity) || 0;
      const rate = parseFloat(updatedItems[index].rate) || 0;
      updatedItems[index].amount = (quantity * rate).toFixed(2);
    }

    setLineItems(updatedItems);
  };

  // Handle product selection from picker popup
  const handleProductSelect = (index, product) => {
    const updatedItems = [...lineItems];
    if (!product) {
      // Cleared
      updatedItems[index] = { ...updatedItems[index], product_service: '', description: '', rate: 0, amount: '0.00' };
    } else {
      const qty = parseFloat(updatedItems[index].quantity) || 1;
      const rate = parseFloat(product.price) || 0;
      updatedItems[index] = {
        ...updatedItems[index],
        product_service: product.name,
        description: product.sales_description || '',
        rate: rate,
        amount: (qty * rate).toFixed(2)
      };
    }
    setLineItems(updatedItems);
  };

  // Handle qty dropdown change
  const handleQtyChange = (index, value) => {
    if (value === 'custom') {
      const updatedItems = [...lineItems];
      updatedItems[index] = { ...updatedItems[index], _useCustomQty: true };
      setLineItems(updatedItems);
    } else {
      handleLineItemChange(index, 'quantity', value);
      const updatedItems = [...lineItems];
      updatedItems[index] = { ...updatedItems[index], _useCustomQty: false };
      // Recalculate amount
      const qty = parseFloat(value) || 0;
      const rate = parseFloat(updatedItems[index].rate) || 0;
      updatedItems[index].amount = (qty * rate).toFixed(2);
      updatedItems[index].quantity = value;
      setLineItems(updatedItems);
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      id: lineItems.length + 1,
      product_service: '',
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      _useCustomQty: false
    }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const handleBillingAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      bill_addr_line1: addressData.address_line1 || '',
      bill_addr_line2: addressData.address_line2 || '',
      bill_addr_city: addressData.address_city || '',
      bill_addr_state: addressData.address_county || '',
      bill_addr_postal_code: addressData.address_postcode || '',
      bill_addr_country: addressData.address_country || 'United Kingdom'
    }));
  };

  const handleTreatmentAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      ship_addr_line1: addressData.address_line1 || '',
      ship_addr_line2: addressData.address_line2 || '',
      ship_addr_city: addressData.address_city || '',
      ship_addr_state: addressData.address_county || '',
      ship_addr_postal_code: addressData.address_postcode || '',
      ship_addr_country: addressData.address_country || 'United Kingdom'
    }));
  };

  // Load customers when opening customer search
  const loadCustomers = async () => {
    setCustomerSearchLoading(true);
    try {
      const userProfile = authService.getCurrentUser();
      const data = await customerService.getCustomers(userProfile.id, 1, 100);
      setCustomers(data.customers || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_email: customer.email || '',
      bill_company_name: customer.company_name || '',
      bill_addr_line1: customer.address_line1 || '',
      bill_addr_line2: customer.address_line2 || '',
      bill_addr_city: customer.address_city || '',
      bill_addr_state: customer.address_county || '',
      bill_addr_postal_code: customer.address_postcode || '',
      bill_addr_country: customer.address_country || 'United Kingdom'
    }));
    setShowCustomerSearch(false);
  };

  // Copy billing address to treatment address
  const copyBillingToTreatment = () => {
    setFormData(prev => ({
      ...prev,
      ship_customer_name: prev.customer_name,
      ship_company_name: prev.bill_company_name,
      ship_addr_line1: prev.bill_addr_line1,
      ship_addr_line2: prev.bill_addr_line2,
      ship_addr_city: prev.bill_addr_city,
      ship_addr_state: prev.bill_addr_state,
      ship_addr_postal_code: prev.bill_addr_postal_code,
      ship_addr_country: prev.bill_addr_country
    }));
  };

  // Open customer search when button is clicked
  useEffect(() => {
    if (showCustomerSearch) {
      loadCustomers();
    }
  }, [showCustomerSearch]);

  // Auto-populate customer data when preselectedCustomer is provided
  useEffect(() => {
    if (preselectedCustomer && !isEditing) {
      setFormData(prev => ({
        ...prev,
        customer_id: preselectedCustomer.id,
        customer_name: preselectedCustomer.name,
        customer_email: preselectedCustomer.email || '',
        bill_company_name: preselectedCustomer.company_name || '',
        bill_addr_line1: preselectedCustomer.address_line1 || '',
        bill_addr_line2: preselectedCustomer.address_line2 || '',
        bill_addr_city: preselectedCustomer.address_city || '',
        bill_addr_state: preselectedCustomer.address_county || '',
        bill_addr_postal_code: preselectedCustomer.address_postcode || '',
        bill_addr_country: preselectedCustomer.address_country || 'United Kingdom',
        // Set treatment address same as billing initially
        ship_addr_line1: preselectedCustomer.address_line1 || '',
        ship_addr_line2: preselectedCustomer.address_line2 || '',
        ship_addr_city: preselectedCustomer.address_city || '',
        ship_addr_state: preselectedCustomer.address_county || '',
        ship_addr_postal_code: preselectedCustomer.address_postcode || '',
        ship_addr_country: preselectedCustomer.address_country || 'United Kingdom',
        ship_customer_name: preselectedCustomer.name,
        ship_company_name: preselectedCustomer.company_name || ''
      }));
    }
  }, [preselectedCustomer, isEditing]);

  // Auto-populate treatment address from job and service date from appointment
  useEffect(() => {
    if (isEditing) return;

    const updates = {};

    // Populate treatment address from job
    if (preselectedJob) {
      updates.ship_addr_line1 = preselectedJob.treatment_address_line1 || '';
      updates.ship_addr_line2 = preselectedJob.treatment_address_line2 || '';
      updates.ship_addr_city = preselectedJob.treatment_address_city || '';
      updates.ship_addr_state = preselectedJob.treatment_address_state || '';
      updates.ship_addr_postal_code = preselectedJob.treatment_address_postcode || '';
      updates.ship_addr_country = preselectedJob.treatment_address_country || 'United Kingdom';
      updates.ship_customer_name = preselectedJob.treatment_contact_name || preselectedCustomer?.name || '';
      updates.ship_company_name = preselectedJob.treatment_contact_company || preselectedCustomer?.company_name || '';
    }

    // Populate document date from appointment
    if (preselectedAppointment?.scheduled_date) {
      updates.doc_date = new Date(preselectedAppointment.scheduled_date).toISOString().split('T')[0];
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }

    // Prefill first line item from job context
    if (preselectedJob || preselectedAppointment) {
      setLineItems(prev => prev.map((item, i) => {
        if (i !== 0) return item;
        const updated = { ...item };

        // Product/service from job category
        if (preselectedJob?.category_name && !item.product_service) {
          updated.product_service = preselectedJob.category_name;
        }

        // Rate and quantity from job price
        if (preselectedJob?.price && !item.rate) {
          updated.rate = parseFloat(preselectedJob.price) || 0;
          // For per-visit pricing, set qty to total visits
          if (preselectedJob.price_type === 'per_visit' && preselectedJob.estimated_visits) {
            updated.quantity = parseInt(preselectedJob.estimated_visits);
          }
          updated.amount = ((parseFloat(updated.quantity) || 1) * updated.rate).toFixed(2);
        }

        // Build description from job context
        if (!item.description && preselectedJob) {
          const descParts = [];
          if (preselectedJob.estimated_visits) {
            if (preselectedJob.price_type === 'per_visit') {
              descParts.push(`${preselectedJob.estimated_visits} visits`);
            } else {
              descParts.push(`Includes ${preselectedJob.estimated_visits} visits`);
            }
          }
          if (preselectedJob.contract_start_date) {
            const d = new Date(preselectedJob.contract_start_date);
            descParts.push(`Contract start: ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`);
          }
          if (descParts.length > 0) updated.description = descParts.join('\n');
        }

        return updated;
      }));
    }
  }, [preselectedJob, preselectedAppointment, isEditing]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    }

    if (!formData.doc_date) {
      newErrors.doc_date = isEstimate ? 'Estimate date is required' : 'Invoice date is required';
    }

    if (!isEstimate && !formData.due_date) {
      newErrors.due_date = 'Due date is required';
    }

    if (lineItems.some(item => !(item.product_service || '').trim())) {
      newErrors.line_items = 'All line items must have a product/service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const totals = calculateTotals();
      const newTotal = parseFloat(totals.total);

      // Strip internal fields from line items; sync service_date to invoice/estimate date
      const cleanLineItems = lineItems.map(({ _useCustomQty, ...item }) => ({
        ...item,
        service_date: formData.doc_date
      }));

      // Build base data without internal field names
      const { doc_number, doc_date, expiry_date, due_date, ...restFormData } = formData;

      let submitData;
      if (isEstimate) {
        submitData = {
          ...restFormData,
          estimate_number: doc_number,
          estimate_date: doc_date,
          expiry_date: expiry_date || null,
          customer_id: preselectedCustomer?.id || formData.customer_id || null,
          job_id: preselectedJob?.id || null,
          appointment_id: preselectedAppointment?.id || null,
          line_items: cleanLineItems,
          subtotal: parseFloat(totals.subtotal),
          tax_rate: 20.00,
          tax_amount: parseFloat(totals.taxAmount),
          total_amount: newTotal
        };
      } else {
        // For edits: adjust balance by the change in total (preserves payments)
        let balanceDue = newTotal;
        if (isEditing && invoice) {
          const oldTotal = parseFloat(invoice.total_amount) || 0;
          const oldBalance = parseFloat(invoice.balance_due) || 0;
          const amountPaid = oldTotal - oldBalance;
          balanceDue = Math.max(0, newTotal - amountPaid);
        }

        submitData = {
          ...restFormData,
          invoice_number: doc_number,
          invoice_date: doc_date,
          due_date: due_date,
          line_items: cleanLineItems,
          subtotal: parseFloat(totals.subtotal),
          tax_amount: parseFloat(totals.taxAmount),
          total_amount: newTotal,
          balance_due: balanceDue
        };
      }

      try {
        await onSubmit(submitData);
      } catch (error) {
        // Error toast handled by caller — keep modal open for retry
      }
    }
  };

  // Handle job category selection from picker
  const handleJobCategorySelect = useCallback((categoryId, categoryPath) => {
    setSelectedJobCategoryId(categoryId);
    setFormData(prev => ({
      ...prev,
      class: categoryPath || ''
    }));
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white">
              {isEditing
                ? isEstimate
                  ? `Edit Estimate${invoice?.estimate_number ? ` - ${invoice.estimate_number}` : ''}`
                  : `Edit Invoice${invoice?.qb_invoice_number ? ` - #${invoice.qb_invoice_number}` : ''}`
                : showCustomerInTitle && preselectedCustomer
                  ? `New ${isEstimate ? 'Estimate' : 'Invoice'} — ${preselectedCustomer.name}`
                  : `Create ${isEstimate ? 'Estimate' : 'New Invoice'}`
              }
            </h2>
            <div className="flex items-center gap-3 mt-0.5">
              {isEditing && formData.doc_number && (
                <p className="text-white text-opacity-80 text-sm">{formData.doc_number}</p>
              )}
              {invoice?.technician_name && (
                <span className="flex items-center gap-1 text-white text-opacity-80 text-sm">
                  <User size={13} /> {invoice.technician_name}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors duration-200 p-1 rounded-full hover:bg-white hover:bg-opacity-20 z-10 relative"
            type="button"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-4 md:p-8">

            {/* Top Section - Invoice Details and Customer Info */}
            <div className="grid grid-cols-1 gap-6 lg:gap-8 mb-6">

              {/* Invoice Details - Full width */}
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-pest-blue">{isEstimate ? 'Estimate Details' : 'Invoice Details'}</h3>
                </div>

                <div className="p-6 space-y-4">
                  {/* Row 1: Date | Due/Expiry Date | Status */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {isEstimate ? 'Estimate Date *' : 'Invoice Date *'}
                      </label>
                      <DateTimePicker
                        value={formData.doc_date}
                        onChange={(val) => handleChange({ target: { name: 'doc_date', value: val } })}
                        dateOnly
                        className="w-full"
                      />
                      {errors.doc_date && <p className="text-red-500 text-xs mt-1">{errors.doc_date}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {isEstimate ? 'Expiry Date' : 'Due Date *'}
                      </label>
                      {isEstimate ? (
                        <DateTimePicker
                          value={formData.expiry_date}
                          onChange={(val) => handleChange({ target: { name: 'expiry_date', value: val } })}
                          dateOnly
                          className="w-full"
                        />
                      ) : (
                        <DateTimePicker
                          value={formData.due_date}
                          onChange={(val) => handleChange({ target: { name: 'due_date', value: val } })}
                          dateOnly
                          showTerms
                          baseDate={formData.doc_date}
                          activeTerm={formData.terms}
                          onTermSelect={(term) => {
                            if (term === null) {
                              setFormData(prev => ({ ...prev, terms: '' }));
                              setDueDateManual(true);
                            } else {
                              handleChange({ target: { name: 'terms', value: term } });
                            }
                          }}
                          className="w-full"
                        />
                      )}
                      {errors.due_date && <p className="text-red-500 text-xs mt-1">{errors.due_date}</p>}
                    </div>
                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors text-sm"
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 3: Job Type — full width popup picker */}
                  <JobCategoryPicker
                    selectedCategoryId={selectedJobCategoryId}
                    onCategorySelect={handleJobCategorySelect}
                  />
                </div>
              </div>

              {/* Customer Information */}
              {!showCustomerInTitle && (
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-pest-blue">Customer Information</h3>
                    <button
                      type="button"
                      onClick={() => setShowCustomerSearch(!showCustomerSearch)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-pest-teal to-teal-600 text-white border-none rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200 hover:from-teal-600 hover:to-teal-700 hover:shadow-lg"
                    >
                      <User size={14} />
                      Select Customer
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="customer_name" className="block text-sm font-medium text-gray-700 mb-2">
                        Customer Name *
                      </label>
                      <input
                        type="text"
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                          errors.customer_name ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                        }`}
                        placeholder="Enter customer name"
                      />
                      {errors.customer_name && <p className="text-red-500 text-sm mt-2">{errors.customer_name}</p>}
                    </div>
                    <div>
                      <label htmlFor="customer_email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="customer_email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                        placeholder="Enter customer email"
                      />
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* Address Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-6">

              {/* Billing Address */}
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-pest-blue">{isEstimate ? 'Billing Address' : 'Invoice To Address'}</h3>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    {/* Read-only customer reference fields */}
                    {formData.customer_name && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Customer Name</label>
                          <p className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded border border-gray-200">{formData.customer_name}</p>
                        </div>
                        {formData.bill_company_name && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Company</label>
                            <p className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded border border-gray-200">{formData.bill_company_name}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {formData.customer_email && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                        <p className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded border border-gray-200">{formData.customer_email}</p>
                      </div>
                    )}

                    <AddressFinder
                      initialAddress={{
                        address_line1: formData.bill_addr_line1,
                        address_line2: formData.bill_addr_line2,
                        address_city: formData.bill_addr_city,
                        address_county: formData.bill_addr_state,
                        address_postcode: formData.bill_addr_postal_code,
                        address_country: formData.bill_addr_country
                      }}
                      onAddressSelect={handleBillingAddressSelect}
                      onStateChange={(state) => setAddressFinderStates(prev => ({ ...prev, billing: state }))}
                      showManualEntry={true}
                      autoSelectSingle={true}

                    />
                  </div>
                </div>
              </div>

              {/* Treatment Address */}
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-pest-blue">Treatment Address</h3>
                    <button
                      type="button"
                      onClick={copyBillingToTreatment}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white border-none rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200 hover:from-gray-600 hover:to-gray-700 hover:shadow-lg"
                    >
                      <Copy size={14} />
                      {isEstimate ? 'Copy from Billing' : 'Copy from Invoice To'}
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="ship_customer_name" className="block text-sm font-medium text-gray-700 mb-2">
                          Contact Name
                        </label>
                        <input
                          type="text"
                          id="ship_customer_name"
                          name="ship_customer_name"
                          value={formData.ship_customer_name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                          placeholder="Contact name"
                        />
                      </div>
                      <div>
                        <label htmlFor="ship_company_name" className="block text-sm font-medium text-gray-700 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          id="ship_company_name"
                          name="ship_company_name"
                          value={formData.ship_company_name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                          placeholder="Company name"
                        />
                      </div>
                    </div>

                    <AddressFinder
                      initialAddress={{
                        address_line1: formData.ship_addr_line1,
                        address_line2: formData.ship_addr_line2,
                        address_city: formData.ship_addr_city,
                        address_county: formData.ship_addr_state,
                        address_postcode: formData.ship_addr_postal_code,
                        address_country: formData.ship_addr_country
                      }}
                      onAddressSelect={handleTreatmentAddressSelect}
                      onStateChange={(state) => setAddressFinderStates(prev => ({ ...prev, treatment: state }))}
                      showManualEntry={true}
                      autoSelectSingle={true}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Line Items Section — 2-row card layout */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-pest-blue">Services & Products</h3>
                  <button
                    type="button"
                    onClick={addLineItem}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-pest-green to-green-600 text-white border-none rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200 hover:from-green-600 hover:to-green-700 hover:shadow-lg"
                  >
                    <Plus size={14} />
                    Add Line Item
                  </button>
                </div>
              </div>

              <div className="p-4 md:p-6">
                {errors.line_items && <p className="text-red-500 text-sm mb-4">{errors.line_items}</p>}

                <div className="space-y-4">
                  {lineItems.map((item, index) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-3 md:p-4 bg-gray-50/50">
                      {/* Row 1: Product/Service | Qty | Rate | Amount | Delete */}
                      <div className="flex flex-col md:flex-row gap-3 mb-3">
                        {/* Product/Service picker popup */}
                        <div className="w-full md:w-56 shrink-0">
                          <ProductServicePicker
                            selectedProductName={item.product_service}
                            onProductSelect={(product) => handleProductSelect(index, product)}
                            label="Product/Service *"
                          />
                        </div>

                        {/* Qty dropdown */}
                        <div className="w-full md:w-20 shrink-0">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Qty</label>
                          {item._useCustomQty ? (
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                              className="w-full px-2.5 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue text-sm"
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            <select
                              value={[1,2,3,4,5,6,7,8,9,10].includes(Number(item.quantity)) ? item.quantity : 'custom'}
                              onChange={(e) => handleQtyChange(index, e.target.value)}
                              className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue text-sm"
                            >
                              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                              <option value="custom">Custom</option>
                            </select>
                          )}
                        </div>

                        {/* Rate */}
                        <div className="w-full md:w-28 shrink-0">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Rate (£)</label>
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                            className="w-full px-2.5 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue text-sm"
                            min="0"
                            step="0.01"
                          />
                        </div>

                        {/* Amount (read-only) */}
                        <div className="w-full md:w-28 shrink-0">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Amount (£)</label>
                          <input
                            type="text"
                            value={parseFloat(item.amount).toFixed(2)}
                            readOnly
                            className="w-full px-2.5 py-2 border border-gray-200 rounded-lg bg-gray-100 text-sm font-medium text-gray-700"
                          />
                        </div>

                        {/* Delete button */}
                        <div className="flex items-end">
                          {lineItems.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => removeLineItem(index)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove line item"
                            >
                              <Trash2 size={16} />
                            </button>
                          ) : (
                            <div className="w-8" />
                          )}
                        </div>
                      </div>

                      {/* Row 2: Description button (opens popup) */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setEditingDescriptionIndex(index)}
                          className={`w-full text-left px-3 py-2 border rounded-lg text-sm transition-colors ${
                            item.description
                              ? 'border-gray-300 bg-white text-gray-700 hover:border-pest-blue'
                              : 'border-dashed border-gray-300 text-gray-400 hover:border-pest-blue hover:text-gray-600'
                          }`}
                        >
                          {item.description
                            ? <span className="line-clamp-2">{item.description}</span>
                            : '+ Add description...'
                          }
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Financial Summary and Notes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-6">

              {/* Notes */}
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-pest-blue">Notes</h3>
                </div>

                <div className="p-6">
                  <textarea
                    id="memo"
                    name="memo"
                    value={formData.memo}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors resize-none"
                    placeholder={isEstimate ? 'Additional notes or memo for this estimate...' : 'Additional notes or memo for this invoice...'}
                  />
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-pest-blue">Financial Summary</h3>
                </div>

                <div className="p-6">
                  {(() => {
                    const totals = calculateTotals();
                    return (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-700">Subtotal:</span>
                          <span className="font-semibold">£{totals.subtotal}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-700">VAT (20%):</span>
                          <span className="font-semibold">£{totals.taxAmount}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-2">
                          <div className="flex justify-between items-center py-2">
                            <span className="text-lg font-semibold text-gray-900">Total:</span>
                            <span className="text-lg font-bold text-pest-blue">£{totals.total}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-3 sm:space-y-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-gradient-to-r from-pest-green to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
                >
                  {isEditing
                    ? (isEstimate ? 'Update Estimate' : 'Update Invoice')
                    : (isEstimate ? 'Create Estimate' : 'Create Invoice')
                  }
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Customer Search Modal */}
        {showCustomerSearch && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Select Customer</h3>
                <button
                  onClick={() => setShowCustomerSearch(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {customerSearchLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="text-gray-500">Loading customers...</div>
                  </div>
                ) : customers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No customers found
                  </div>
                ) : (
                  <div className="space-y-2">
                    {customers.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer)}
                        className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-gray-900">{customer.name}</div>
                            {customer.company_name && (
                              <div className="text-sm text-gray-600">{customer.company_name}</div>
                            )}
                            {customer.email && (
                              <div className="text-sm text-gray-500">{customer.email}</div>
                            )}
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            {customer.address_line1 && (
                              <div>{customer.address_line1}</div>
                            )}
                            {customer.address_postcode && (
                              <div>{customer.address_postcode}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => setShowCustomerSearch(false)}
                  className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Description Editor Popup with voice + AI enhance */}
        {editingDescriptionIndex !== null && (
          <TextAreaPopup
            label="Edit Description"
            fieldKey="invoice_description"
            value={lineItems[editingDescriptionIndex]?.description || ''}
            onChange={(val) => handleLineItemChange(editingDescriptionIndex, 'description', val)}
            placeholder="Enter a detailed description of the service..."
            isOpen={true}
            onOpen={() => {}}
            onClose={() => setEditingDescriptionIndex(null)}
          />
        )}
      </div>
    </div>
  );
};

export default InvoiceForm;
